// AddAdsModal.js
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Modal from './Modal';
const Container = styled.div`
  padding: 32px 40px;
`;

const TypeSelector = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
`;

const TypeButton = styled.button`
  flex: 1;
  padding: 16px 24px;
  border: 2px solid ${props => props.selected ? '#4A90E2' : '#e0e0e0'};
  border-radius: 12px;
  background-color: ${props => props.selected ? '#EBF3FE' : '#fff'};
  color: ${props => props.selected ? '#4A90E2' : '#666'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.2s;
  font-size: 1.1rem;
  font-weight: 500;
  
  &:hover {
    background-color: ${props => props.selected ? '#EBF3FE' : '#f8f9fa'};
    border-color: ${props => props.selected ? '#4A90E2' : '#4A90E2'};
  }

  svg {
    font-size: 1.5rem;
  }
`;

const UploadSection = styled.div`
  display: flex;
  gap: 32px;
  align-items: flex-start;
  justify-content: center;
`;

const FileUploadArea = styled.div`
  flex: 1;
  max-width: 440px;  // 미리보기 영역과 동일한 크기
  border: 2px dashed #e0e0e0;
  border-radius: 12px;
  padding: 40px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background-color: #fafafa;
  height: ${props => {
    switch(props.type) {
      case 'main':
        return '247px';  // 16:9 비율에 맞는 높이
      case 'banner':
        return '160px';  // 2.75:1 비율에 맞는 높이
      case 'side':
        return '783px';  // 9:16 비율에 맞는 높이
      default:
        return '247px';
    }
  }};
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  &:hover {
    border-color: #4A90E2;
    background-color: #f8f9fa;
  }
`;

const UploadIcon = styled.div`
  width: 40px;
  height: 40px;
  background-color: #4A90E2;
  border-radius: 50%;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 1.5rem;
    color: white;
  }
`;

const UploadText = styled.p`
  color: #666;
  font-size: 1.1rem;
  margin: 8px 0;
`;

const FileName = styled.p`
  color: #4A90E2;
  font-size: 1.1rem;
  font-weight: 500;
  margin: 8px 0;
`;

const RatioGuide = styled.div`
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin: 24px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  
  svg {
    font-size: 24px;
    color: #4A90E2;
  }
`;

const GuideText = styled.p`
  color: #666;
  font-size: 1rem;
  margin: 0;
  line-height: 1.5;
`;

const PreviewContainer = styled.div`
  flex: 1;
  max-width: 440px;  // FileUploadArea와 동일한 크기
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background-color: #f8f9fa;
  height: ${props => {
    switch(props.type) {
      case 'main':
        return '247px';  // 16:9 비율에 맞는 높이 (440 * 9/16)
      case 'banner':
        return '160px';  // 2.75:1 비율에 맞는 높이 (440 / 2.75)
      case 'side':
        return '783px';  // 9:16 비율에 맞는 높이 (440 * 16/9)
      default:
        return '247px';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  
  img, video {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const TimeInputContainer = styled.div`
  margin: 32px 0;
`;

const InputLabel = styled.label`
  display: block;
  color: #333;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 8px;
`;

const TimeInput = styled.input`
  width: 160px;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1.1rem;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #4A90E2;
  }
  
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    opacity: 1;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #eee;
`;

const Button = styled.button`
  padding: 14px 32px;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
  
  ${props => props.primary ? `
    background-color: #4A90E2;
    color: white;
    border: none;
    
    &:hover {
      background-color: #357ABD;
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(0);
    }
  ` : `
    background-color: #f5f5f5;
    color: #666;
    border: 2px solid #e0e0e0;
    
    &:hover {
      background-color: #e5e5e5;
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(0);
    }
  `}
`;

const AddAdsModal = ({ isOpen, onClose, type, onConfirm }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [mediaType, setMediaType] = useState('image');
    const [duration, setDuration] = useState(5);

    // 모달이 열릴 때마다 상태 초기화
    useEffect(() => {
        if (isOpen) {
            // 파일 관련 상태 초기화
            setSelectedFile(null);
            setPreviewUrl('');
            // 미디어 타입 초기화
            setMediaType('image');
            // 지속 시간 초기화
            setDuration(5);
        }
    }, [isOpen]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const getTypeInfo = () => {
        switch (type) {
            case 'main':
                return {
                    title: '런닝머신앱 대기화면 추가',
                    ratio: '16:9',
                    acceptTypes: mediaType === 'image' ? 'image/*' : 'video/*'
                };
            case 'banner':
                return {
                    title: '티비앱 추가',
                    ratio: '2.75:1',
                    acceptTypes: 'image/*'
                };
            case 'side':
                return {
                    title: '런닝머신앱 운동장 추가',
                    ratio: '9:16',
                    acceptTypes: 'image/*'
                };
            default:
                return { title: '', ratio: '', acceptTypes: '' };
        }
    };

    const handleSubmit = () => {
        if (!selectedFile) {
            alert('파일을 선택해주세요.');
            return;
        }
        if (!duration || duration == 0) {
            alert('광고 시간을 설정하세요.');
            return;
        }

        onConfirm(selectedFile, type, mediaType, duration);
    };

    const typeInfo = getTypeInfo();

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={typeInfo.title}
            shouldCloseOnOverlayClick={false}
        >
            <Container>
                {type === 'main' && (
                    <TypeSelector>
                        <TypeButton 
                            selected={mediaType === 'image'}
                            onClick={() => setMediaType('image')}
                        >
                            사진
                        </TypeButton>
                        <TypeButton 
                            selected={mediaType === 'video'}
                            onClick={() => setMediaType('video')}
                        >
                            동영상
                        </TypeButton>
                    </TypeSelector>
                )}

                <RatioGuide>
                    <GuideText>
                        권장 비율: {typeInfo.ratio}
                    </GuideText>
                </RatioGuide>

                <input
                    type="file"
                    id="fileInput"
                    accept={typeInfo.acceptTypes}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />

                <UploadSection>
                    <FileUploadArea onClick={() => document.getElementById('fileInput').click()}>
                        {selectedFile ? (
                            <FileName>{selectedFile.name}</FileName>
                        ) : (
                            <>
                                <UploadText>클릭하여 파일을 선택하세요</UploadText>
                                {/* <UploadText style={{ fontSize: '0.9rem', color: '#999' }}>
                                    또는 파일을 이곳에 드래그하세요
                                </UploadText> */}
                            </>
                        )}
                    </FileUploadArea>

                    {selectedFile && (
                        <PreviewContainer>
                            {mediaType === 'image' || type !== 'main' ? (
                                <img 
                                    src={previewUrl} 
                                    alt="미리보기"
                                />
                            ) : (
                                <video 
                                    src={previewUrl} 
                                    controls
                                />
                            )}
                        </PreviewContainer>
                    )}
                </UploadSection>

                {type === 'main' && (
                    <TimeInputContainer>
                        <InputLabel htmlFor="duration">지속 시간</InputLabel>
                        <TimeInput
                            id="duration"
                            type="number"
                            min="1"
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                        />
                        <span style={{ marginLeft: '8px', color: '#666' }}>초</span>
                    </TimeInputContainer>
                )}

                <ButtonContainer>
                    <Button onClick={onClose}>
                        취소
                    </Button>
                    <Button primary onClick={handleSubmit}>
                        추가하기
                    </Button>
                </ButtonContainer>
            </Container>
        </Modal>
    );
};

export default AddAdsModal;